/**
 * Serviço de cache inteligente para otimizar chamadas de API
 * Evita recarregamentos desnecessários de dados que mudam pouco
 */

class CacheService {
  constructor() {
    this.cache = new Map();
    this.inFlight = new Map();
    this.defaultTTL = 10 * 60 * 1000; // 10 minutos em milissegundos
  }

  /**
   * Definir TTL específico para diferentes tipos de dados - otimizado para performance
   */
  getTTL(key) {
    const ttlConfig = {
      'products': 30 * 60 * 1000,      // 30 minutos - produtos mudam pouco
      'finance_products': 30 * 60 * 1000, // 30 minutos - produtos para finance
      'users': 15 * 60 * 1000,         // 15 minutos - usuários mudam moderadamente
      'roles': 60 * 60 * 1000,         // 1 hora - roles mudam raramente
      'permissions': 60 * 60 * 1000,   // 1 hora - permissões mudam raramente
      'enums': 60 * 60 * 1000,         // 1 hora - enums raramente mudam
      'dashboard_analytics': 10 * 60 * 1000, // 10 minutos - dashboard pode ser menos atual
      'analytics': 5 * 60 * 1000,      // 5 minutos - analytics específicos mais atuais
      'finance_enums': 60 * 60 * 1000, // 1 hora - enums financeiros raramente mudam
    };

    for (const [type, ttl] of Object.entries(ttlConfig)) {
      if (key.includes(type)) {
        return ttl;
      }
    }

    return this.defaultTTL;
  }

  /**
   * Obter dados do cache (retorna mesmo expirado para SWR)
   */
  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    const now = Date.now();
    const isExpired = now > item.expires;
    if (isExpired) {
      // Não removemos imediatamente: padrão stale-while-revalidate
      // Consumidores receberão dado antigo enquanto revalidamos em background
      return item.data;
    }
    return item.data;
  }

  /**
   * Armazenar dados no cache
   */
  set(key, data, customTTL = null) {
    const ttl = customTTL || this.getTTL(key);
    const expires = Date.now() + ttl;

    this.cache.set(key, {
      data,
      expires,
      created: Date.now(),
      isRefreshing: false
    });

    console.log(`Cache set: ${key}, expires in ${Math.round(ttl / 1000)}s`);
  }

  /**
   * Invalidar cache por chave específica
   */
  invalidate(key) {
    this.cache.delete(key);
    console.log(`Cache invalidated: ${key}`);
  }

  /**
   * Invalidar cache por padrão
   */
  invalidatePattern(pattern) {
    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => {
      this.cache.delete(key);
      console.log(`Cache invalidated by pattern: ${key}`);
    });
  }

  /**
   * Limpar todo o cache
   */
  clear() {
    this.cache.clear();
    console.log('Cache cleared');
  }

  /**
   * Obter estatísticas do cache
   */
  getStats() {
    const now = Date.now();
    let validItems = 0;
    let expiredItems = 0;

    for (const [key, item] of this.cache.entries()) {
      if (now > item.expires) {
        expiredItems++;
      } else {
        validItems++;
      }
    }

    return {
      totalItems: this.cache.size,
      validItems,
      expiredItems,
      memoryUsage: this.cache.size * 100 // Estimativa básica
    };
  }

  /**
   * Wrapper para chamadas de API com cache automático
   */
  async withCache(key, apiCall, customTTL = null) {
    const now = Date.now();
    const item = this.cache.get(key);

    if (item) {
      const isExpired = now > item.expires;
      if (!isExpired) {
        // Dado válido
        return item.data;
      }
      // Expirado: retornar stale e revalidar em background
      if (!item.isRefreshing) {
        item.isRefreshing = true;
        const refreshPromise = Promise.resolve()
          .then(() => apiCall())
          .then((data) => {
            this.set(key, data, customTTL);
            const updated = this.cache.get(key);
            if (updated) updated.isRefreshing = false;
          })
          .catch((error) => {
            const updated = this.cache.get(key);
            if (updated) updated.isRefreshing = false;
            console.error(`Error refreshing cache key ${key}:`, error);
          });
        this.inFlight.set(key, refreshPromise);
        // Cleanup after refresh completes
        refreshPromise.finally(() => this.inFlight.delete(key));
      }
      return item.data;
    }

    // Sem item no cache: deduplicar chamadas concorrentes
    if (this.inFlight.has(key)) {
      return this.inFlight.get(key);
    }

    const fetchPromise = Promise.resolve()
      .then(() => {
        console.log(`Cache miss, fetching: ${key}`);
        return apiCall();
      })
      .then((data) => {
        this.set(key, data, customTTL);
        return data;
      })
      .catch((error) => {
        console.error(`Error fetching data for cache key ${key}:`, error);
        throw error;
      });

    this.inFlight.set(key, fetchPromise);
    try {
      return await fetchPromise;
    } finally {
      this.inFlight.delete(key);
    }
  }
}

// Instância singleton do serviço de cache
export const cacheService = new CacheService();

// Helper functions para uso comum
export const withCache = (key, apiCall, ttl = null) => {
  return cacheService.withCache(key, apiCall, ttl);
};

export const invalidateCache = (keyOrPattern) => {
  if (keyOrPattern.includes('*') || keyOrPattern.includes('pattern:')) {
    const pattern = keyOrPattern.replace('pattern:', '').replace('*', '');
    cacheService.invalidatePattern(pattern);
  } else {
    cacheService.invalidate(keyOrPattern);
  }
};

export const clearAllCache = () => {
  cacheService.clear();
};

export default cacheService;